// hooks/useAgentDetailsPage.js

import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";


import { useAgentQuery } from "../queries/agents/useAgentsQuery";
import { useLeadScoring } from "./useLeadScoring";
import { useWidgetSettings } from "./useWidgetSettings";
import useLeadsFilter from "./useLeadsFilter";
import { usePropertyStore } from "../store/propertyStore";
import { useLeadsQuery } from "../queries/leads/useLeadsQuery";

import { useAgentStore } from "../store/agentStore";
import { normalizeLeads } from "../utils/normalizeLeads";

export default function useAgentDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { setSelectedAgent } = useAgentStore();

  const { data: agentDetails, isLoading: isAgentLoading } = useAgentQuery(id);

  const { setSelectedProperty } = usePropertyStore();

  useEffect(() => {
    if (agentDetails) {
      setSelectedAgent(agentDetails);
    }
  }, [agentDetails, setSelectedAgent]);

  /* MODALS */
  const [activeForm, setActiveForm] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [activeTab, setActiveTab] = useState("leadscore");

  /* FILTERS */
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterProperties, setFilterProperties] = useState("All");
  const [filterClient, setFilterClient] = useState("All");
  const [filterSource, setFilterSource] = useState("All");
  const [filterIntent, setFilterIntent] = useState("All");
  const [sortDate, setSortDate] = useState("Latest");

  const { data: leadsList, isLoading: isLeadsLoading } = useLeadsQuery();
  const leads = useMemo(() => normalizeLeads(leadsList), [leadsList]);

  const filteredLeads = useLeadsFilter(
    leads,
    filterCategory,
    filterProperties,
    filterClient,
    filterSource,
    sortDate,
    filterIntent
  );

  const agents = useMemo(
    () => ["All", ...new Set(leads.map((lead) => lead.clientName))],
    [leads]
  );

  const properties = useMemo(
    () => ["All", ...new Set(leads.map((lead) => lead.property_name))],
    [leads]
  );

  /* ACTIONS */
  const openChatboxModal = () => setActiveForm("chatbox");
  const openLeadScoreModal = () => setActiveForm("leadscore");



  const closeModal = () => {
    setShowSettingsModal(false);
    setActiveForm(null);
    setActiveTab(null);
  };

  const handleSettingsOption = (option) => {
    setShowSettingsModal(false);

    if (option === "leadscore") openLeadScoreModal();
    else if (option === "chatbox") openChatboxModal();
    else {
      setActiveForm("both");
      setActiveTab("leadscore");
    }
  };

  const { isLoading: isScoringLoading, saveScoring } =
    useLeadScoring(id, closeModal);

  const {
    isLoading: isWidgetSettingsLoading,
    handleManageWidgetSettings,
  } = useWidgetSettings(id, closeModal);

  return {
    navigate,
    agentDetails,

    /* loading */
    isLoading:
      isAgentLoading ||
      isLeadsLoading ||
      isScoringLoading ||
      isWidgetSettingsLoading,

    /* modal */
    activeForm,
    showSettingsModal,
    activeTab,
    setShowSettingsModal,
    setActiveTab,
    closeModal,
    handleSettingsOption,

    /* actions */
    openChatboxModal,
    openLeadScoreModal,

    /* leads */
    filteredLeads,
    filters: {
      filterCategory,
      setFilterCategory,
      filterProperties,
      setFilterProperties,
      properties,
      filterClient,
      setFilterClient,
      agents,
      filterSource,
      setFilterSource,
      filterIntent,
      setFilterIntent,
      sortDate,
      setSortDate,
    },

    /* handlers */
    saveScoring,
    handleManageWidgetSettings,

    setSelectedProperty,
  };
}